import React from 'react';
import { Navigate } from 'react-router-dom';
import { isHandheld } from '../utils/device';
import { buildMenuGroups } from '../utils/navMenu';
import { pathToPermCode } from '../utils/pagePermissions';

/**
 * Role-based route protection.
 *
 * The dynamic page permissions (role_page_permissions table) are the primary
 * source of truth for what each role can access. This file provides fallback
 * protection and default routes.
 *
 * The Sidebar uses the /page-permissions/my-permissions API to determine
 * which menu items to show. This canAccessRoute function is used as a
 * fallback guard on the route itself.
 */

// Paths that are ALWAYS blocked for non-admin roles (true admin-only features)
const ADMIN_ONLY = new Set([
  '/settings', '/admin', '/saas-billing', '/cms', '/pdf-editor',
]);

// Paths accessible to ALL authenticated users (public pages, portals)
const UNIVERSAL = new Set([
  '/', '/dashboard', '/login', '/register', '/help', '/home',
  '/test-catalog', '/contact', '/page', '/kiosk', '/user-settings',
]);

// A destination, but never a HOME: a second audience's portal, a kiosk
// station, a preferences screen. Mirrors NOT_A_LANDING_PAGE in
// app/routes/page_permissions.py, which is the authority — this list only
// covers the case where the server gave us no answer at all.
const NOT_A_LANDING_PAGE = new Set([
  'home', 'contact', 'help', 'test-catalog', 'store',
  'patient', 'patient-portal', 'client-portal', 'lab-portal',
  'kiosk', 'my-account', 'user-settings', 'cms/builder',
  'purchasing/orders/new', 'clients/onboard',
]);

/**
 * Where this login goes when it asks for nothing in particular.
 *
 * THE DASHBOARD IS NOT COMPULSORY. This used to return '/dashboard' for
 * every role that was not a patient, and the catch-all route sends anything
 * unrecognised here — so unticking View on Dashboard did not hide a page, it
 * bricked the role: sign in, get sent to /dashboard, and be refused by the
 * same permission that hid it. Every client role in the matrix was in exactly
 * that state.
 *
 * The order of preference:
 *   1. the collector app, if they hold the profile — that IS the job;
 *   2. the landing page resolved by the server (their own, else their
 *      role's, else the first page the role can open);
 *   3. the first item on their own sidebar they can actually view;
 *   4. /dashboard, only as a last resort and only when nothing is known.
 */
export function getDefaultRoute(role, { isCollector = false, defaultPage = '', permissions = null } = {}) {
  // A mobile collector's home is the collector app ON A HAND-HELD. It wins
  // over the role default and over any landing page an administrator set,
  // because holding the profile is a statement about what this person does
  // all day — not a preference to be overridden by a screen they never open.
  //
  // ON A COMPUTER IT DOES NOT. "when a external client logs in with a phone
  // default the screen to the mobile view, however when the external client
  // logs in using a pc or mac then default the view would be the default lab
  // view not mobile". The collector app is one narrow column with big targets
  // and a camera; on a 27-inch monitor that is not the right answer, and the
  // full application is a tap away in the sidebar either way.
  if (isCollector && isHandheld()) return '/collect';
  const r = (role || '').toLowerCase();
  if (r === 'patient') return '/patient';

  const viewable = (code) => {
    if (!permissions) return null;          // not loaded — no opinion
    const perm = permissions[code];
    return perm ? !!perm.can_view : null;   // unknown code — no opinion
  };

  // What the server resolved for this person. Refused only when we KNOW the
  // page is denied; an unknown code passes, exactly as canViewPath does.
  const dp = String(defaultPage || '').trim();
  if (dp.startsWith('/') && viewable(pathToPermCode(dp)) !== false) return dp;

  if (r === 'superadmin' || r === 'admin' || r === 'platform_admin') return '/dashboard';
  if (!permissions) return '/dashboard';
  if (viewable('dashboard')) return '/dashboard';

  for (const group of buildMenuGroups({ userRole: r, isCollector })) {
    for (const item of (group.items || [])) {
      const code = pathToPermCode(item.path);
      if (!code || NOT_A_LANDING_PAGE.has(code)) continue;
      if (viewable(code)) return item.path;
    }
  }
  return '/dashboard';
}

/**
 * Check if a role can access a given route path.
 * This is a PERMISSIVE check — the dynamic page permissions (fetched from
 * /page-permissions/my-permissions) are the real source of truth.
 * This function only blocks truly admin-only pages for non-admin roles.
 */
export function canAccessRoute(role, path) {
  const r = (role || '').toLowerCase();

  // Superadmin, admin, and platform_admin can access everything.
  // platform_admin is the cross-tenant platform-wide admin (is_global=TRUE)
  // — same effective access as superadmin; was being incorrectly blocked
  // from /settings, /admin, /saas-billing, etc.
  if (r === 'superadmin' || r === 'admin' || r === 'platform_admin') return true;

  // Universal pages — everyone can access
  if (!path || path === '' || UNIVERSAL.has(path)) return true;

  // Check if path starts with a universal prefix
  for (const u of UNIVERSAL) {
    if (u !== '/' && path.startsWith(u + '/')) return true;
  }

  // Admin-only pages — block non-admins
  for (const a of ADMIN_ONLY) {
    if (path === a || path.startsWith(a + '/')) return false;
  }

  // The separate-audience portals. `/portal` and `/rep` authenticate against
  // their OWN account tables with their own tokens and are rendered outside
  // the staff shell — a staff role has no bearing on them, so this guard must
  // not have an opinion. It used to refuse `/portal` for role=patient, which
  // is the one role whose portal it is.
  if (path === '/portal' || path === '/rep'
      || path.startsWith('/portal/') || path.startsWith('/rep/')) return true;

  // Patient role: only portal, store, and universal pages
  if (r === 'patient') {
    const patientPaths = ['/patient', '/store'];
    for (const p of patientPaths) {
      if (path === p || path.startsWith(p + '/')) return true;
    }
    return false;
  }

  // For all other roles (tech, provider, client, custom roles):
  // Allow access — the dynamic page permissions from the DB will handle
  // fine-grained control via the Sidebar and my-permissions API.
  return true;
}

export function getVisibleMenuItems(role) {
  const r = (role || '').toLowerCase();
  if (r === 'superadmin' || r === 'admin' || r === 'platform_admin') return null; // null = show all
  // Return null to let the dynamic permissions system handle filtering
  // The Sidebar will use /page-permissions/my-permissions for non-admin roles
  return null;
}

export default function ProtectedRoute({ children, user, allowedRoles }) {
  if (!user) return <Navigate to="/login" replace />;

  const role = (user.role || '').toLowerCase();

  // Admin bypasses all checks
  if (role === 'admin' || role === 'superadmin') return children;

  // If specific allowedRoles provided, check against them
  if (allowedRoles && allowedRoles.length > 0) {
    if (!allowedRoles.includes(role)) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', textAlign: 'center' }}>
          <div>
            <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }}>403</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary, #111827)', marginBottom: 8 }}>Access Denied</div>
            <div style={{ fontSize: 14, color: 'var(--text-secondary, #6b7280)', marginBottom: 20 }}>You don't have permission to view this page.</div>
            <a href={getDefaultRoute(role)} style={{ padding: '8px 20px', background: '#2563eb', color: '#fff', borderRadius: 8, textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>
              Go to Dashboard
            </a>
          </div>
        </div>
      );
    }
  }

  return children;
}
